import { useMemo } from 'react';
import { Mail, Send, AlertTriangle, UserMinus } from 'lucide-react';
import { AnalyticsKPITile } from '@/components/analytics/AnalyticsKPITile';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { PartnerCampaignOverviewRow } from '@/hooks/useEnterpriseEmailMonitoring';

/**
 * M3 (#573): email at-a-glance tiles, aggregated from `partner_campaign_overview`.
 *
 * Reuses `AnalyticsKPITile` (the canonical KPI primitive). NO sparkline in v1
 * (deferred — net-new Recharts infra). Failure % and unsub % are volume-weighted
 * across campaigns (sum of failed / sum of sent), not a naive average of rates,
 * so a tiny campaign can't skew the rollup.
 */

interface EmailAtAGlanceProps {
  campaigns: PartnerCampaignOverviewRow[];
  /** This card's own query state (campaignsQuery). */
  isLoading: boolean;
  error: Error | null;
}

interface EmailAggregate {
  sent: number;
  failurePct: number;
  unsubPct: number;
}

export function aggregateEmailGlance(
  campaigns: PartnerCampaignOverviewRow[],
): EmailAggregate {
  let sent = 0;
  let failed = 0;
  let unsubWeighted = 0;
  for (const c of campaigns) {
    sent += c.emails_sent;
    failed += c.emails_failed;
    // unsubscribe_rate is a 0..1 fraction; weight by this campaign's sent volume.
    unsubWeighted += c.unsubscribe_rate * c.emails_sent;
  }
  const denom = sent + failed;
  return {
    sent,
    failurePct: denom > 0 ? (100 * failed) / denom : 0,
    unsubPct: sent > 0 ? (100 * unsubWeighted) / sent : 0,
  };
}

export function EmailAtAGlance({
  campaigns,
  isLoading,
  error,
}: EmailAtAGlanceProps): JSX.Element {
  const agg = useMemo(() => aggregateEmailGlance(campaigns), [campaigns]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Mail className="h-5 w-5" />
          Email at a glance
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <AnalyticsKPITile
            variant="secondary"
            label="Emails sent"
            value={agg.sent}
            deltaPct={null}
            format="integer"
            icon={Send}
            isLoading={isLoading}
            error={error}
          />
          <AnalyticsKPITile
            variant="secondary"
            label="Failure rate"
            value={agg.failurePct}
            deltaPct={null}
            format="percent"
            icon={AlertTriangle}
            isLoading={isLoading}
            error={error}
          />
          <AnalyticsKPITile
            variant="secondary"
            label="Unsubscribe rate"
            value={agg.unsubPct}
            deltaPct={null}
            format="percent"
            icon={UserMinus}
            isLoading={isLoading}
            error={error}
          />
        </div>
      </CardContent>
    </Card>
  );
}
